import React, { useState } from 'react';
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Divider,
  TextField,
  Chip,
  Alert,
  Snackbar,
  Tooltip,
  Zoom,
  Slide
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingBag as ShoppingBagIcon,
  ArrowBack as ArrowBackIcon,
  LocalShipping as ShippingIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import { useCart } from '../context/CartContext';
// import './CartPage.css';

const CartItemCard = styled(motion(Paper))(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(198, 164, 63, 0.2)',
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#C6A43F',
    boxShadow: '0 10px 30px rgba(198, 164, 63, 0.1)',
  }
}));

const SummaryCard = styled(Paper)({
  padding: '24px',
  background: 'linear-gradient(135deg, #0A0F1E 0%, #1A1F2E 100%)',
  borderRadius: '16px',
  color: 'white',
  position: 'sticky',
  top: '100px',
});

const CartPage = () => {
  const navigate = useNavigate();
  const { 
    cartItems, 
    removeFromCart, 
    updateQuantity,
    getCartTotal,
    getCartCount
  } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleQuantityChange = (item, delta) => {
    const newQuantity = item.quantity + delta;
    if (newQuantity > 0) {
      updateQuantity(item.id, newQuantity);
    }
  };

  const handleRemoveItem = (itemId) => {
    removeFromCart(itemId);
    setSnackbar({
      open: true,
      message: 'Item removed from cart',
      severity: 'info'
    });
  };

  const handleApplyCoupon = () => {
    if (couponCode.toUpperCase() === 'MIRAE10') {
      setCouponApplied(true);
      setSnackbar({
        open: true,
        message: 'Coupon applied successfully! 10% discount',
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Invalid coupon code',
        severity: 'error'
      });
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const subtotal = getCartTotal();
  const discount = couponApplied ? subtotal * 0.1 : 0;
  const shipping = subtotal > 50000 ? 0 : 500;
  const total = subtotal - discount + shipping;

  if (cartItems.length === 0) {
    return (
      <Box className="cart-page-empty">
        <Container maxWidth="lg" sx={{ py: 12 }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center' }}
          >
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <ShoppingBagIcon sx={{ fontSize: 120, color: 'rgba(198, 164, 63, 0.2)', mb: 3 }} />
            </motion.div>
            <Typography variant="h3" sx={{ fontFamily: 'Cormorant Garamond', color: '#0A0F1E', mb: 2 }}>
              Your Cart is Empty
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', mb: 4, maxWidth: 500, mx: 'auto' }}>
              Discover our exquisite collection of fine jewelry and find the perfect piece that speaks to you.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/shop')}
              sx={{
                bgcolor: '#C6A43F',
                color: '#0A0F1E',
                px: 6,
                py: 2,
                '&:hover': { bgcolor: '#B3923A' }
              }}
            >
              Continue Shopping
            </Button>
          </motion.div>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="cart-page">
      <Container maxWidth="lg" sx={{ py: 12 }}>
        {/* Header */}
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate('/shop')} sx={{ color: '#C6A43F' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h3" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 700, color: '#0A0F1E' }}>
              Shopping Cart
            </Typography>
            <Chip
              label={`${getCartCount()} Items`}
              sx={{
                bgcolor: alpha('#C6A43F', 0.1),
                color: '#C6A43F',
                fontWeight: 600,
                ml: 2
              }}
            />
          </Box>
        </motion.div>

        <Grid container spacing={4}>
          {/* Cart Items */}
          <Grid item xs={12} md={8}>
            <AnimatePresence>
              {cartItems.map((item, index) => (
                <Zoom in={true} key={item.id} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Box>
                    <CartItemCard
                      initial={{ opacity: 0, x: -50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 50 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Grid container spacing={3} alignItems="center">
                        {/* Product Image */}
                        <Grid item xs={12} sm={3}>
                          <Box
                            component={motion.div}
                            whileHover={{ scale: 1.05 }}
                            sx={{
                              width: '100%',
                              height: 150,
                              borderRadius: '12px',
                              overflow: 'hidden',
                              border: '1px solid rgba(198, 164, 63, 0.2)'
                            }}
                          >
                            <img
                              src={item.images?.[0] || '/placeholder.jpg'}
                              alt={item.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                        </Grid>

                        {/* Product Details */}
                        <Grid item xs={12} sm={5}>
                          <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, mb: 1 }}>
                            {item.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Chip
                              size="small"
                              label={item.metalPurity}
                              sx={{ bgcolor: alpha('#C6A43F', 0.1), color: '#C6A43F' }}
                            />
                            <Chip
                              size="small"
                              label={item.category}
                              sx={{ bgcolor: alpha('#C6A43F', 0.1), color: '#C6A43F' }}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.description?.substring(0, 100)}...
                          </Typography>
                        </Grid>

                        {/* Quantity & Price */}
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography variant="h6" sx={{ color: '#C6A43F', fontWeight: 700, mb: 2 }}>
                              ₹{(item.finalPrice * item.quantity).toLocaleString()}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleQuantityChange(item, -1)}
                                  sx={{
                                    bgcolor: alpha('#C6A43F', 0.1),
                                    '&:hover': { bgcolor: '#C6A43F', color: 'white' }
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              </motion.div>
                              
                              <Typography sx={{ minWidth: 40, textAlign: 'center' }}>
                                {item.quantity}
                              </Typography>
                              
                              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleQuantityChange(item, 1)}
                                  sx={{
                                    bgcolor: alpha('#C6A43F', 0.1),
                                    '&:hover': { bgcolor: '#C6A43F', color: 'white' }
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </motion.div>
                            </Box>

                            <Tooltip title="Remove from cart">
                              <IconButton
                                onClick={() => handleRemoveItem(item.id)}
                                sx={{ color: '#f44336' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                      </Grid>
                    </CartItemCard>
                  </Box>
                </Zoom>
              ))}
            </AnimatePresence>

            {/* Continue Shopping */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/shop')}
                sx={{ color: '#C6A43F', mt: 2 }}
              >
                Continue Shopping
              </Button>
            </motion.div>
          </Grid>

          {/* Order Summary */}
          <Grid item xs={12} md={4}>
            <Slide direction="left" in={true} timeout={800}>
              <SummaryCard>
                <Typography variant="h5" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 700, mb: 3, color: '#C6A43F' }}>
                  Order Summary
                </Typography>

                {/* Subtotal */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography color="rgba(255,255,255,0.7)">Subtotal</Typography>
                  <Typography fontWeight={600}>₹{subtotal.toLocaleString()}</Typography>
                </Box>

                {/* Discount */}
                {couponApplied && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, color: '#4CAF50' }}>
                    <Typography>Discount (10%)</Typography>
                    <Typography>-₹{discount.toLocaleString()}</Typography>
                  </Box>
                )}

                {/* Shipping */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography color="rgba(255,255,255,0.7)">Shipping</Typography>
                  <Typography fontWeight={600}>
                    {shipping === 0 ? 'Free' : `₹${shipping}`}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2, borderColor: 'rgba(198,164,63,0.3)' }} />

                {/* Total */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" sx={{ color: 'white' }}>Total</Typography>
                  <Typography variant="h5" sx={{ color: '#C6A43F', fontWeight: 700 }}>
                    ₹{total.toLocaleString()}
                  </Typography>
                </Box>

                {/* Coupon Code */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    sx={{
                      mb: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(198,164,63,0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: '#C6A43F',
                        },
                      }
                    }}
                  />
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleApplyCoupon}
                    disabled={couponApplied}
                    sx={{
                      borderColor: '#C6A43F',
                      color: '#C6A43F',
                      '&:hover': {
                        borderColor: '#B3923A',
                        backgroundColor: 'rgba(198,164,63,0.1)'
                      }
                    }}
                  >
                    {couponApplied ? 'Coupon Applied' : 'Apply Coupon'}
                  </Button>
                </Box>

                {/* Checkout Button */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleCheckout}
                    sx={{
                      bgcolor: '#C6A43F',
                      color: '#0A0F1E',
                      py: 2,
                      mb: 3,
                      '&:hover': {
                        bgcolor: '#B3923A',
                      }
                    }}
                  >
                    Proceed to Checkout
                  </Button>
                </motion.div>

                {/* Trust Badges */}
                <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3 }}>
                  <Tooltip title="Free Shipping on orders above ₹50,000">
                    <Box sx={{ textAlign: 'center' }}>
                      <ShippingIcon sx={{ color: '#C6A43F', fontSize: 28 }} />
                      <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Free Shipping
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Secure Payments">
                    <Box sx={{ textAlign: 'center' }}>
                      <SecurityIcon sx={{ color: '#C6A43F', fontSize: 28 }} />
                      <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Secure
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Hallmarked Jewelry">
                    <Box sx={{ textAlign: 'center' }}>
                      <VerifiedIcon sx={{ color: '#C6A43F', fontSize: 28 }} />
                      <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Hallmarked
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </SummaryCard>
            </Slide>
          </Grid>
        </Grid>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CartPage;